/**
 * RegenControlBar — the pause / stop / resume strip for a single-section
 * regenerate. Rendered above the spine while a regen is in flight or paused.
 *
 * Separate from ``SpineControlBar`` (which controls the DRAFTING run) because a
 * regen runs on a completed run and never enters the reducer's mid-run state —
 * the shell stays in ``post-run``. Its lifecycle is owned by ``useRegenFlow``.
 * Stop and Pause both keep the section's previous draft (the server restores
 * it); Pause additionally lets the writer Resume, which restarts the regen with
 * the same guidance. Pure presentation.
 */

import type React from "react";
import { Button } from "../../ds/components";
import type { RegenActivity } from "../hooks/useRegenFlow";

interface RegenControlBarProps {
  /** Show the "current draft is still in your document" note inside the bar. */
  withNotice?: boolean;
  activity: RegenActivity | null;
  onPause: () => void;
  onStop: () => void;
  /** Finish the section now from what has been gathered. */
  onDraftNow?: () => void;
  onResume: () => void;
  onDiscard: () => void;
}


const RegenControlBar: React.FC<RegenControlBarProps> = ({
  activity,
  onPause,
  onStop,
  onDraftNow,
  onResume,
  onDiscard,
  withNotice = true,
}) => {
  if (!activity) return null;

  const { phase } = activity;
  const isPaused = phase === "paused";
  const busy = phase === "pausing" || phase === "stopping" || phase === "finishing";
  const section = activity.sectionLabel ?? activity.sectionId;
  const resumeFailed = isPaused && Boolean(activity.error);
  const headline = isPaused ? `Paused: ${section}` : `Regenerating ${section}`;
  const status = resumeFailed
    ? `Couldn't resume (${activity.error}). Try Resume again.`
    : isPaused
      ? "Resume restarts this section."
      : phase === "pausing"
        ? "Pausing…"
        : phase === "stopping"
          ? "Stopping…"
          : phase === "finishing"
            ? "Drafting from what was gathered…"
            : null;

  // Design board 16: a header block above the spine with the run controls,
  // and a note that the current draft stays in the document meanwhile.
  return (
    <output className="as-spinehead as-regenbar" aria-live="polite">
      <div className="as-spinehead-top">
        <h1 className="as-spinehead-title">{headline}</h1>
      </div>
      {status ? <p className="as-note">{status}</p> : null}
      <div className="as-spinehead-actions">
        {isPaused ? (
          <>
            <Button variant="primary" size="sm" onClick={onResume}>
              Resume
            </Button>
            <Button variant="secondary" size="sm" onClick={onDiscard}>
              Discard
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onPause} disabled={busy}>
              Pause
            </Button>
            <Button variant="danger" size="sm" onClick={onStop} disabled={busy}>
              Stop and keep current draft
            </Button>
            {onDraftNow && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDraftNow}
                disabled={busy}
                title="Stop retrieving and draft this section now from what has been gathered"
              >
                Draft now
              </Button>
            )}
          </>
        )}
      </div>
      {withNotice && <RegenNotice />}
    </output>
  );
};

/** "The current draft is still in your document" (design board 16). On the
 *  spine it sits under the rows; elsewhere it rides in the bar. */
export const RegenNotice: React.FC = () => (
  <div className="as-notice as-notice-info as-regen-notice">
    <b>The current draft is still in your document</b>
    <div>When the new draft is ready it replaces the current one at the section&apos;s heading.</div>
  </div>
);

export default RegenControlBar;
